import { useState, useEffect } from 'react';
import { Badge, Box, Flex, IconButton, Button } from '@chakra-ui/react';
import { MdDelete, MdEdit, MdSearch } from 'react-icons/md';

import Page from '../components/common/Page';
import { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import StrongInput from '../components/common/StrongInput';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import EditEndPageModal from '../components/endPages/EditEndPageModal';

const COLUMNS = [
  {
    key: 'programId',
    label: 'Program',
    render: (value: any) => value?.title ?? '—',
  },
  { key: 'title', label: 'Title' },
  {
    key: 'nextProgramId',
    label: 'Next Program',
    render: (value: any) => value?.title ?? '—',
  },
  {
    key: 'enableRestart',
    label: 'Restart Enabled',
    render: (value: boolean) => (
      <Badge colorScheme={value ? 'green' : 'gray'}>{value ? 'Enabled' : 'Disabled'}</Badge>
    ),
  },
];

const {
  getEndPages: getEndPagesUrl,
  createEndPage: createEndPageUrl,
  editEndPage: editEndPageUrl,
  deleteEndPage: deleteEndPageUrl,
  getProgramTitles: getProgramTitlesUrl,
} = urlConstants.endPages;

const EndPages = () => {
  const [endPages, setEndPages] = useState<any[]>([]);
  const [programTitles, setProgramTitles] = useState<{ _id: string; title: string }[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedEndPage, setSelectedEndPage] = useState<any | undefined>();

  const showToast = useToastNotification();

  const { loading: isLoadingEndPages, sendRequest: getEndPages } = useApi<any>();
  const { sendRequest: getProgramTitles } = useApi<any>();
  const { sendRequest: createEndPage } = useApi<any>();
  const { sendRequest: editEndPage } = useApi<any>();
  const { sendRequest: deleteEndPage } = useApi<any>();

  const fetchEndPages = () => {
    getEndPages({
      callback: (data: any, error: string | null) => {
        if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
        if (!data) return;
        setEndPages(data.endPages ?? []);
        const total = data.totalCount ?? 0;
        setTotalCount(total);
        setTotalPages(Math.ceil(total / pageSize) || 1);
      },
      command: ApiCommand.GET,
      url: getEndPagesUrl,
      options: { page: currentPage, limit: pageSize },
    });
  };

  const fetchProgramTitles = () => {
    getProgramTitles({
      callback: (data: any, error: string | null) => {
        if (error || !data) return;
        setProgramTitles(data);
      },
      command: ApiCommand.GET,
      url: getProgramTitlesUrl,
    });
  };

  useEffect(() => {
    fetchEndPages();
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchProgramTitles();
  }, []);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onSave = (formData: Record<string, any>) => {
    if (selectedEndPage) {
      editEndPage({
        callback: (_data: any, error: string | null) => {
          if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
          showToast({ title: 'Success', description: 'End page updated', status: 'success' });
          setIsOpenModal(false);
          fetchEndPages();
        },
        command: ApiCommand.PUT,
        url: editEndPageUrl,
        options: { ...formData, id: selectedEndPage._id },
      });
    } else {
      createEndPage({
        callback: (_data: any, error: string | null) => {
          if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
          showToast({ title: 'Success', description: 'End page created', status: 'success' });
          setIsOpenModal(false);
          fetchEndPages();
        },
        command: ApiCommand.POST,
        url: createEndPageUrl,
        options: formData,
      });
    }
  };

  const onDelete = (id: string) => {
    deleteEndPage({
      callback: (_data: any, error: string | null) => {
        if (error) { showToast({ title: 'Failed', description: error, status: 'error' }); return; }
        showToast({ title: 'Success', description: 'End page deleted', status: 'success' });
        fetchEndPages();
      },
      command: ApiCommand.DELETE,
      url: `${deleteEndPageUrl}?id=${id}`,
    });
  };

  const onEdit = (id: string) => {
    setSelectedEndPage(endPages.find((ep) => ep._id === id));
    setIsOpenModal(true);
  };

  return (
    <Page>
      <Flex justifyContent='space-between' alignItems='center' mb={6} gap={4} flexWrap='wrap'>
        <Box flex='1' maxW='400px'>
          <StrongInput placeholder='Search' rightIcon={<MdSearch color='gray.500' />} />
        </Box>
        <Button
          colorScheme='primary'
          px={6}
          py={4}
          borderRadius='md'
          fontWeight='bold'
          _hover={{ bg: 'primary.300' }}
          onClick={() => { setSelectedEndPage(undefined); setIsOpenModal(true); }}
        >
          Create End Page
        </Button>
      </Flex>

      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box backgroundColor='white' borderRadius='lg' height='calc(100vh - 275px)'>
          <StrongTable
            columns={COLUMNS}
            data={endPages}
            loading={isLoadingEndPages}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='Edit'
                  icon={<MdEdit />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={4}
                  onClick={() => onEdit(row._id)}
                />
                <IconButton
                  aria-label='Delete'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  onClick={() => onDelete(row._id)}
                />
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditEndPageModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSave}
        selectedEndPage={selectedEndPage}
        programTitles={programTitles}
      />
    </Page>
  );
};

export default EndPages;
